import type { Connector } from '@avalara/types/index';
import type { DataState } from '@avalara/types/reducer-states';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { CountryModel, RegionModel } from 'types/avatax';
import type { MarketingData } from 'types/marketing';

const initialState: DataState = {
  connectors: {
    data: [],
    error: '',
  },
  countries: [],
  isLoading: false,
  regions: {},
  requests: 0,
  marketingData: {
    lso: 'Direct Visitor',
    lsmr: 'Direct Visitor',
    salesRepId: '',
  },
  abandonedSignupTracker: null,
  showingLoadingRocket: false,
  featuresLoaded: false,
};

type SetRegionsPayload = { regions: Array<RegionModel>; country: string };
type SetMarketingDataFieldPayload = { field: keyof MarketingData; data: string };

const dataStore = createSlice({
  name: 'data',
  initialState,
  reducers: {
    loadingDataStart: (state) => {
      state.isLoading = true;
      state.requests += 1;
    },

    loadingDataFinish: (state) => {
      state.requests -= 1;
      state.isLoading = state.requests > 0;
    },

    receiveConnectorsSuccess: (state, action: PayloadAction<Array<Connector>>) => {
      state.connectors = { data: action.payload, error: '' };
    },

    receiveConnectorsFail: (state, action: PayloadAction<string>) => {
      state.connectors = { data: [], error: action.payload };
    },

    receiveMarketingData: (state, action: PayloadAction<MarketingData>) => {
      state.marketingData = action.payload;
    },

    setAbandonedSignupTracker: (state, action: PayloadAction<() => void>) => {
      state.abandonedSignupTracker = action.payload;
    },

    setCountriesData: (state, action: PayloadAction<Array<CountryModel>>) => {
      state.countries = action.payload;
    },

    setMarketingDataField: (state, action: PayloadAction<SetMarketingDataFieldPayload>) => {
      state.marketingData[action.payload.field] = action.payload.data;
    },

    setRegionsData: (state, action: PayloadAction<SetRegionsPayload>) => {
      state.regions[action.payload.country] = action.payload.regions;
    },

    setShowingLoadingRocket: (state, action: PayloadAction<boolean>) => {
      state.showingLoadingRocket = action.payload;
    },

    setFeaturesLoaded: (state) => {
      state.featuresLoaded = true;
    },
  },
});

export const {
  loadingDataStart,
  loadingDataFinish,
  receiveConnectorsSuccess,
  receiveConnectorsFail,
  receiveMarketingData,
  setAbandonedSignupTracker,
  setCountriesData,
  setMarketingDataField,
  setRegionsData,
  setShowingLoadingRocket,
  setFeaturesLoaded,
} = dataStore.actions;

export default dataStore;
